import { ProjectDeliveryType, ProjectWorkLoad } from '@platform/client/schemas';
import { MultiSelectDropdownOption } from '@platform/components';
import { createContext } from 'react';

export const CreateProjectContext = createContext({
  businessChallenge: '',
  setBusinessChallenge: (_challenge: string) => {},
  skills: new Set([] as SkillType[]),
  setSkills: (_skills: Set<SkillType>) => {},
  supportTypes: new Set([] as SupportType[]),
  setSupportTypes: (_supportTypes: Set<SupportType>) => {},
});

export const BUSINESS_CHALLENGES = [
  {
    name: 'Product Market Fit',
    description:
      'I need help improving my product and overall business strategy, including business model generation.',
    value: 'productMarketFit',
  },
  {
    name: 'Branding & Positioning',
    description:
      'I need help identifying my ideal customer, creating value propositions, and developing my go-to-market plan.',
    value: 'brandingPositioning',
  },
  {
    name: 'Marketing',
    description:
      'I need help developing a marketing strategy, communication plan, reviewing our social media channels, defining our target audience or storytelling strategy.',
    value: 'marketing',
  },
  {
    name: 'Sales',
    description: 'I need help finding sales leads, nurturing them, and closing more pitches.',
    value: 'sales',
  },
  {
    name: 'Customer Success',
    description:
      'I need help improving the quality and/or efficiency of our customer onboarding, customer service, and customer evangelism.',
    value: 'customerSuccess',
  },
  {
    name: 'Reporting & Communicating Impact',
    description:
      'I need help developing a Theory of Change, capturing impact data, and then using it to grow my revenues & impact.',
    value: 'reportingCommunicatingImpact',
  },
  {
    name: 'Raising Capital',
    description:
      'I need help developing a fundraising and investment strategy, finding potential investors, and pitching them.',
    value: 'raisingCapital',
  },
  {
    name: 'Human Resources',
    description:
      'I need to build systems to create a better employee experience, including things like pay equity, performance plans or help to design a training program.',
    value: 'humanResources',
  },
  {
    name: 'Business Operations',
    description:
      'I need help operating more effectively and supporting general management of my enterprise.',
    value: 'businessOperations',
  },
  {
    name: 'Accounting & Finance',
    description:
      'I need help building models and improving my accounting, billing, and payroll systems.',
    value: 'accountingFinance',
  },
  {
    name: 'Legal',
    description:
      'I need help developing contracts and processes, including things like GDPR, to build more partnerships.',
    value: 'legal',
  },
  // TODO: Selecting this needs to make us prompt for a specific business challenge on the Final Details page
  {
    name: 'Other',
    description: 'Let us know what business challenge you are facing.',
    value: 'other',
  },
] as const;

export type BusinessChallengeName = (typeof BUSINESS_CHALLENGES)[number]['name'];
export type BusinessChallengeValue = (typeof BUSINESS_CHALLENGES)[number]['value'];

// TODO: Change to ProjectRoleType from ProjectSpecs.py
export const SUPPORT_TYPES = ['Trainer', 'Call', 'Mentor', 'Coach', 'Consultant'] as const;
export type SupportType = (typeof SUPPORT_TYPES)[number];
type SupportTypeDetail = {
  description: string;
  tooltip: string;
  examples: {
    title: string;
    description: string;
    country: string;
    isRemote?: boolean;
  }[];
};

// Reference: apps/legacy/config/lang/en/Opportunities/RoleTypes.json
export const SUPPORT_TYPE_DETAILS: Record<(typeof SUPPORT_TYPES)[number], SupportTypeDetail> = {
  Trainer: {
    description: 'Develops materials and facilitates training to help you and your team grow.',
    tooltip:
      "Typically, 2-8 hours of team-based training over one to four sessions but something you'll co-design with your experteer.",
    examples: [
      {
        title: 'Train the Kueeza Team on Marketing Strategies',
        description: 'Support Kuueza with advertising, brand strategy and writing',
        country: 'Ghana',
      },
      {
        title: 'Train the Club Tiossane team on Digital/Technical Capacities (ERP Odoo)',
        description: 'Support Club Tiossane with database management, it and technology',
        country: 'Senegal',
      },
      {
        title: 'Train the Victoria Relief Foundation Team on How to Compress Videos',
        description: 'Support Victoria Relief Foundation with multimedia',
        country: 'United States',
      },
    ],
  },
  Call: {
    description:
      'Provides feedback, shares expertise, or provides guidance on a targeted business challenge.',
    tooltip: 'Typically a one-time, 30-60 minute call',
    examples: [
      {
        title:
          'Social Impact Project for 30min Call with Human Connections to Help with Launching E-commerce Store',
        description:
          'Support Human Connections with web development, business strategy and operations',
        country: 'United States',
      },
      {
        title: '30min call with NABU to help with Social Media',
        description: 'Support NABU with social media',
        country: 'United States',
      },
      {
        title: '30min call with Meli Bees Network gUG to help with International Laws',
        description: 'Support Meli Bees Network gUG with legal',
        country: 'Germany',
      },
    ],
  },
  Mentor: {
    description:
      'Shares their expertise and practical know-how to help you grow your skills, make better decisions, and gain new perspectives.',
    tooltip:
      "Typically, 1-2 mentor sessions per month over 3-6 months, but something you'll co-design with your experteer.",
    examples: [
      {
        title: 'Provide Mentorship to Support the R&D team with the Innovation Strategy',
        description: 'Support Sibö with innovation, life sciences and product design',
        country: 'Netherlands',
      },
      {
        title: 'Mentoring Support to Refine a Prototype Idea',
        description:
          'Support MindTerra with brand strategy, business strategy and design (product or industrial)',
        country: 'Thailand',
      },
      {
        title: 'Provide Mentorship on Leadership in NGO/Social Business Context',
        description: 'Support Learning Lions with leadership and management',
        country: 'Kenya',
      },
    ],
  },
  Coach: {
    description:
      'Helps you think through challenges and arrive at your own solutions related to growing your business, team, and/or personal development.',
    tooltip:
      "Typically, 1-2 hours per week of coaching over 2, 3, or 4 months, but something you'll co-design with your experteer.",
    examples: [
      {
        title: 'Coach for Baeru Environmental Services',
        description: 'Provide Baeru Environmental Services with coaching support',
        country: 'India',
        isRemote: true,
      },
      {
        title: 'Coach for Takataka Ni Mali',
        description: 'Provide Takataka Ni Mali with coaching support',
        country: 'Kenya',
        isRemote: true,
      },
      {
        title: 'Coach for Stonestep AG',
        description: 'Provide Stonestep AG with coaching support',
        country: 'Switzerland',
        isRemote: true,
      },
    ],
  },
  Consultant: {
    description:
      'Provides information, makes recommendations, and assists with the implementation of solutions.',
    tooltip:
      "Typically, 2 weeks - 6 months in a part-time or full-time capacity, but something you'll co-design with your experteer.",
    examples: [
      {
        title: 'Digital Marketing Experteer to Launch Our First Upcycled Product',
        description: 'Support Ashaya with brand design, brand strategy and copywriting',
        country: 'India',
      },
      {
        title: 'Fundraising/Business Strategy Expert to Support our Business Model',
        description: 'Support Takataka Ni Mali with business strategy, fundraising and strategy',
        country: 'Kenya',
        isRemote: true,
      },
      {
        title: 'Product and Engineering Experteer for Data Skills Education Delivery',
        description:
          'Support Eskwelabs with database management, product management and technology',
        country: 'Philippines',
      },
    ],
  },
} as const;

// TODO: canonical list
export const LANGUAGES = [
  'English',
  'Acholi',
  'Afrikaans',
  'Akan',
  'Albanian',
  'Amharic',
  'Arabic',
  'Armenian',
  'Assyrian',
  'Azerbaijani',
  'Azeri',
  'Bajuni',
  'Bambara',
  'Basque',
  'Behdini',
  'Belorussian',
  'Bengali',
  'Berber',
  'Bosnian',
  'Bravanese',
  'Bulgarian',
  'Burmese',
  'Cantonese',
  'Catalan',
  'Chaldean',
  'Chaochow',
  'Chamorro',
  'Chavacano',
  'Cherokee',
  'Chuukese',
  'Croatian',
  'Czech',
  'Dakota',
  'Danish',
  'Dari',
  'Dinka',
  'Diula',
  'Dutch',
  'Ewe',
  'Farsi',
  'Fijian Hindi',
  'Finnish',
  'Flemish',
  'French',
  'French Canadian',
  'Fukienese',
  'Fula',
  'Fulani',
  'Fuzhou',
  'Gaddang',
  'Georgian',
  'German',
  'Gorani',
  'Greek',
  'Gujarati',
  'Haitian Creole',
  'Hakka',
  'Hakka - China',
  'Hausa',
  'Hebrew',
  'Hindi',
  'Hmong',
  'Hunanese',
  'Hungarian',
  'Ibanag',
  'Ibo / Igbo',
  'Icelandic',
  'Ilocano',
  'Indonesian',
  'Italian',
  'Jakartanese',
  'Japanese',
  'Javanese',
  'Karen',
  'Kashmiri',
  'Kazakh',
  'Khmer (Cambodian)',
  'Kinyarwanda',
  'Kirghiz',
  'Kirundi',
  'Korean',
  'Kosovan',
  'Krio',
  'Kurdish',
  'Kurmanji',
  'Lakota',
  'Laotian',
  'Latvian',
  'Lingala',
  'Lithuanian',
  'Luganda',
  'Luxembourgeois',
  'Maay',
  'Macedonian',
  'Malagasy',
  'Malay',
  'Malayalam',
  'Maltese',
  'Mandarin',
  'Mandingo',
  'Mandinka',
  'Maninka',
  'Mankon',
  'Marathi',
  'Marshallese',
  'Mien',
  'Mina',
  'Mirpuri',
  'Mixteco',
  'Moldavan',
  'Mongolian',
  'Montenegrin',
  'Navajo',
  'Neapolitan',
  'Nepali',
  'Nigerian Pidgin English',
  'Norwegian',
  'Nuer',
  'Oromo',
  'Pahari',
  'Pampangan',
  'Pamgasinan',
  'Pashto',
  'Patois',
  'Pidgin English',
  'Polish',
  'Portuguese',
  'Portuguese Creole',
  'Punjabi',
  'Romanian',
  'Russian',
  'Samoan',
  'Serbian',
  'Shanghainese',
  'Shona',
  'Sicilian',
  'Sinhalese',
  'Sindhi',
  'Slovak',
  'Slovenian',
  'Somali',
  'Sorani',
  'Spanish',
  'Sudanese Arabic',
  'Swahili',
  'Swedish',
  'Sylhetti',
  'Tagalog',
  'Taiwanese',
  'Tajik',
  'Tamil',
  'Telugu',
  'Thai',
  'Tibetan',
  'Tigre',
  'Tigrinya',
  'Toishanese',
  'Tongan',
  'Tshiluba',
  'Turkish',
  'Twi',
  'Ukrainian',
  'Urdu',
  'Uzbek',
  'Vietnamese',
  'Visayan',
  'Welsh',
  'Wolof',
  'Yiddish',
  'Yoruba',
  'Yupik',
] as const;

export type LanguagesType = (typeof LANGUAGES)[number];

// TODO: Create the right skills list
export const SKILLS = [
  'Account Management',
  'Accounting',
  'Advertising',
  'Brand Strategy',
  'Business Innovation',
  'Business Strategy',
  'Civil Engineering',
  'Coaching',
  'Communications',
  'Compliance/ Regulatory',
  'Copywriting',
  'Customer Success',
  'Data Analysis',
  'Data Science',
  'Data Transformation and Reporting',
  'Database Management',
  'Electrical Engineering',
  'Entrepreneurship',
  'Finance',
  'Fundraising (Equity)',
  'Fundraising (Grants)',
  'Graphic Design',
  'Human Resources',
  'IT Administration',
  'Impact Investing',
  'Industrial Design',
  'International Business',
  'Investing',
  'Leadership Development',
  'Legal',
  'Manufacturing Operations',
  'Marketing',
  'Mechanical Engineering',
  'Monitoring and Evaluation',
  'Multimedia Editing',
  'Network and Cybersecurity',
  'Operations Management',
  'Procurement',
  'Product Management',
  'Project Management',
  'Public Relations',
  'Quality Assurance',
  'Robotics & AI',
  'Sales',
  'Social Media Marketing',
  'Software Development',
  'Software Quality Assurance',
  'Solution Architecture',
  'Storytelling',
  'Supply Chain and Logistics',
  'Training and Facilitation',
  'Translation',
  'UI/UX Design',
  'User/Market Research',
  'Vendor Management',
  'Web/Mobile Development',
] as const;

export type SkillType = (typeof SKILLS)[number];

export const SKILL_OPTIONS: MultiSelectDropdownOption[] = SKILLS.map((skill) => ({
  value: skill,
  label: skill,
}));

export type ProjectFormValues = {
  languages: LanguagesType[];
  projectPhoto: string; // TODO: Correct type
  workWithTeam: boolean;
  /* Trainer */
  trainingTopicRichText: string;
  trainerAdditionalRequirements: string;
  trainerLocation: ProjectDeliveryType;
  /* Call */
  callOutcomesRichText: string;
  /* Mentor */
  mentorOutcomesRichText: string;
  /* Coach */
  coachOutcomesRichText: string;
  // TODO: the Participation Agreement blurb, which mentions EY for some reason
  /* Consultant */
  projectTitle: string;
  expectedDeliverablesRichText: string;
  consultantAdditionalRequirements: string;
  projectTimeCommitment: ProjectWorkLoad;
  projectBkgRichText: string;
  consultantLocation: ProjectDeliveryType;
  learningOutcomesRichText: string;
};

/* Used to determine different copy/behavior based on whether this details form is being
 * shown during initial rep creation, a rep editing the project, or an admin editing the project */
export type ProjectDetailsMode = 'rep-create' | 'rep-edit' | 'admin';
